// Firestore CRUD for approval limits used by SystemConfig and CAF workflow.

import { db, serverTimestamp } from '@/firebase'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  writeBatch,
  doc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore'

/** Utility: normalize an id from role (stable, URL-safe) */
function idFromRole(role = '') {
  return String(role).trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')
}

/** Utility: coerce limit string -> number | 'unlimited' */
function normalizeLimit(v) {
  if (v === 'unlimited' || v === 'Unlimited') return 'unlimited'
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** List all limits (sorted for UI): active first by `order`, then role; then inactive. */
export async function listApprovalLimitsFS() {
  const col = collection(db, 'approvalLimits')
  const q = query(col, orderBy('order', 'asc'), orderBy('role', 'asc'))
  const snap = await getDocs(q)

  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))

  rows.sort((a, b) => {
    const aActive = a.isActive !== false
    const bActive = b.isActive !== false
    if (aActive !== bActive) return aActive ? -1 : 1
    const ao = Number(a.order ?? 0)
    const bo = Number(b.order ?? 0)
    if (ao !== bo) return ao - bo
    return String(a.role || '').localeCompare(String(b.role || ''))
  })

  return rows
}

/**
 * DANGEROUS: full replacement publish (kept for reference).
 * Do not call this from your UI if you don’t want to erase existing rows.
 */
export async function replaceAllApprovalLimitsFS(rows = []) {
  const colRef = collection(db, 'approvalLimits')

  // Delete everything, then set new docs
  const existing = await getDocs(colRef)
  const batch = writeBatch(db)
  existing.forEach(d => batch.delete(d.ref)) // ← wipes the collection
  rows.forEach((r, idx) => {
    const id = r.id || idFromRole(r.role)
    const ref = doc(db, 'approvalLimits', id)
    batch.set(ref, {
      role: String(r.role || '').trim(),
      limit: normalizeLimit(r.limit),
      order: Number(r.order ?? idx),
      isActive: r.isActive !== false,
      updatedAt: serverTimestamp(),
      createdAt: r.createdAt || serverTimestamp(),
    })
  })
  await batch.commit()
  return true
}

/** Upsert a single limit row (safe for add/edit without affecting others). */
export async function upsertApprovalLimitFS(row) {
  const id = row.id || idFromRole(row.role)
  const ref = doc(db, 'approvalLimits', id)
  const body = {
    role: String(row.role || '').trim(),
    limit: normalizeLimit(row.limit),
    order: Number(row.order ?? 0),
    isActive: row.isActive !== false,
    updatedAt: serverTimestamp(),
    ...(row.createdAt ? { createdAt: row.createdAt } : { createdAt: serverTimestamp() }),
  }
  await setDoc(ref, body, { merge: true })
  return id
}

/** Delete a single limit row by id. */
export async function deleteApprovalLimitFS(id) {
  if (!id) return
  await deleteDoc(doc(db, 'approvalLimits', id))
}

/**
 * SAFE PUBLISH: merge/append a set of rows without deleting existing ones.
 * - For each staged row, upsert by (id || normalized role).
 * - Existing rows that are *not* in the staged set are left untouched.
 */
export async function publishApprovalLimitsFS(rows = []) {
  const batch = writeBatch(db)
  rows.forEach((r, idx) => {
    const id = r.id || idFromRole(r.role)
    const ref = doc(db, 'approvalLimits', id)
    batch.set(ref, {
      role: String(r.role || '').trim(),
      limit: normalizeLimit(r.limit),
      order: Number(r.order ?? idx),
      isActive: r.isActive !== false,
      updatedAt: serverTimestamp(),
      createdAt: r.createdAt || serverTimestamp(),
    }, { merge: true })
  })
  await batch.commit()
  return true
}

/**
 * For workflow: fetch active limits, sorted ASC by numeric limit,
 * with 'unlimited' treated as Infinity.
 */
export async function getActiveLimitsForWorkflowFS() {
  const col = collection(db, 'approvalLimits')
  const qActive = query(col, where('isActive', '==', true))
  const snap = await getDocs(qActive)

  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))

  rows.sort((a, b) => {
    const av = a.limit === 'unlimited' ? Number.POSITIVE_INFINITY : Number(a.limit ?? 0)
    const bv = b.limit === 'unlimited' ? Number.POSITIVE_INFINITY : Number(b.limit ?? 0)
    if (av !== bv) return av - bv
    const ao = Number(a.order ?? 0)
    const bo = Number(b.order ?? 0)
    if (ao !== bo) return ao - bo
    return String(a.role || '').localeCompare(String(b.role || ''))
  })

  return rows
}